// Agentlar — xsib-core `agent` moduli bilan haqiqiy kontrakt.
// Manba: `agent/internal/AgentController.java`, `AgentMoneyController.java`.
//
// ⚠️ AGENT ⊂ RESELLER (egasi qarori 2026-08-21, ADR-029):
//   AGENT    — naqd qabul qiladi va komissiya oladi
//   RESELLER — agentning HAMMASI, ustiga o'z narx ro'yxati
// Shuning uchun bitta daraxt, lekin `kind` ustuni bilan.
// Uchinchi tur (`BOTH`) olib tashlandi — u RESELLER bilan bir xil
// to'plamni bildirardi (migratsiya V202608730001).

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { operationResultSchema, type OperationResult } from '@/shared/api/operationResult'
import { MAX_PAGE_SIZE, serverPageSchema } from '@/shared/api/serverPage'
import {
  debtLimitSchema,
  instantSchema,
  moneySchema,
  toMoney,
  toOptionalMoney,
  uuidSchema,
} from '@/shared/api/schemas'
import type { Money } from '@/shared/lib/money'

export const AGENT_KINDS = ['AGENT', 'RESELLER'] as const
export type AgentKind = (typeof AGENT_KINDS)[number]

export const AGENT_STATES = ['ACTIVE', 'SUSPENDED', 'CLOSED'] as const
export type AgentState = (typeof AGENT_STATES)[number]

export const SETTLEMENT_MODES = ['DEPOSIT', 'POSTPAID'] as const
export type SettlementMode = (typeof SETTLEMENT_MODES)[number]

export const agentNodeSchema = z.object({
  id: uuidSchema,
  tenantId: uuidSchema,
  parentId: uuidSchema.nullish(),
  /** `ltree` matn shakli, masalan "root.tashkent.chilonzor" */
  path: z.string(),
  code: z.string(),
  displayName: z.string().nullish(),
  /**
   * ⚠️ `null` BO'LISHI MUMKIN va bu qat'iy tekshiruv bilan SAHIFANI
   * YIQITARDI (2026-08-13 da tuzatildi).
   *
   * Tugun turi 2026-08-12 dan beri Java enum'i emas — u dinamik jadval
   * (`platform.org_node_type`, QARORLAR.md §19.3). Backenddagi `AgentKind`
   * shu servis modellaydigan IKKITA hisob-kitob turining nomi bo'lib qoldi
   * va noma'lum tur uchun ataylab `null` qaytaradi (`AgentKind.fromCode`
   * javadoc'i: "`RESELLER` emas, chunki u komissiya va o'z narxini rost
   * qilib qo'yardi — yopiq ishlash qoidasi").
   *
   * O'sha javadoc panel haqida aniq va'da beradi: «Panelda u bo'sh katak
   * bo'lib ko'rinadi, sahifa esa ochiladi». Bu yerdagi `z.enum(...)` esa
   * va'dani buzardi: administrator `is_settlement_party = true` bilan yangi
   * tur ochib bitta tugun yaratsa, `GET /api/agents` javobi zod'da yiqilib
   * BUTUN Agentlar sahifasini o'ldirardi — ya'ni §19.3 ning bosh
   * xususiyatini ko'rsatish sahifani buzardi.
   */
  kind: z.enum(AGENT_KINDS).nullish(),
  /**
   * ⚠️ `null` bo'lishi mumkin — yuqoridagi bilan bir xil sabab:
   * `settlement_mode` faqat `is_settlement_party = true` turlarda ma'noli
   * (QARORLAR.md §19.3), qolganlarida `NULL` qoladi.
   */
  settlementMode: z.enum(SETTLEMENT_MODES).nullish(),
  /**
   * Tugunga bog'langan sheriklardan birortasida kassa yoqilganmi.
   *
   * ⚠️ Maydon YO'Q bo'lishi mumkin — bu «bog'langan foydalanuvchi yo'q»,
   * ya'ni «bilmaymiz». `false` bilan chalkashtirmang: `false` — bog'langan
   * sherik BOR, lekin kassasi yo'q degan ANIQ da'vo.
   */
  cashDesk: z.boolean().nullish(),
  state: z.enum(AGENT_STATES),
  createdAt: instantSchema.nullish(),
  updatedAt: instantSchema.nullish(),
})

export type AgentNodeRaw = z.infer<typeof agentNodeSchema>

export interface AgentNode {
  id: string
  parentId: string | null
  path: string
  code: string
  displayName: string
  /** `null` — «bu tur shu servis modellaydigan uchtadan biri emas» (sxema izohi) */
  kind: AgentKind | null
  /** `null` — bu tur bilan hisob-kitob qilinmaydi (sxema izohi) */
  settlementMode: SettlementMode | null
  /** `null` — tugunga bog'langan foydalanuvchi yo'q, ya'ni «bilmaymiz» */
  cashDesk: boolean | null
  state: AgentState
  /** Daraxtdagi chuqurlik — `path` dagi nuqtalar soni bo'yicha */
  depth: number
  /**
   * ISO-8601; `null` — server vaqtni BERMADI.
   *
   * ⚠️ `PartyNodeRepository` `created_at` ni javobga QO'SHADI (SQL
   * o'qilgan: `kind, settlement_mode, state, created_at, updated_at`),
   * lekin ilgari panel uni tashlab yuborardi. Ro'yxatda esa aynan shu
   * maydon «oxirgi qo'shilgan birinchi» degan tartibning yagona
   * asosi.
   *
   * ⚠️ `null` ni «hozir» deb to'ldirish TAQIQ: o'shanda vaqti
   * noma'lum tugunlar ro'yxat boshini egallardi va tartib ishonarli
   * ko'rinishda yolg'on bo'lardi.
   */
  createdAt: string | null
}

export function toAgentNode(raw: AgentNodeRaw): AgentNode {
  return {
    id: raw.id,
    parentId: raw.parentId ?? null,
    path: raw.path,
    code: raw.code,
    displayName: raw.displayName ?? raw.code,
    // ⚠️ `?? 'AGENT'` QO'YMANG: taxminiy tur "komissiya oladi" degan
    // YOLG'ONNI ekranga chiqarardi. Noma'lum — noma'lum bo'lib qolsin.
    kind: raw.kind ?? null,
    settlementMode: raw.settlementMode ?? null,
    cashDesk: raw.cashDesk ?? null,
    state: raw.state,
    depth: raw.path.split('.').length,
    createdAt: raw.createdAt ?? null,
  }
}

/**
 * Ildiz tugunlar.
 *
 * ⚠️ NEGA `GET /api/agents` (butun ro'yxat) ISHLATILMAYDI: u `q` parametrisiz
 * chaqirilganda GLOBAL doiradagi foydalanuvchi uchun **401** qaytaradi
 * (`guard.scopeFilter` GLOBAL doirani qayta ishlay olmaydi va xato
 * ushlanmagani uchun tanasiz 401 ga aylanadi). `q` berilganda esa ishlaydi.
 * Shuning uchun daraxt `roots` + `children` orqali quriladi, qidiruv esa
 * `q` bilan — ikkalasi ham ishonchli yo'llar.
 */
export async function fetchAgentRoots(): Promise<AgentNode[]> {
  const response = await api.get<unknown>('/api/agents/roots')
  return z.array(agentNodeSchema).parse(response.data).map(toAgentNode)
}

export async function fetchAgentChildren(id: string): Promise<AgentNode[]> {
  const response = await api.get<unknown>(`/api/agents/${id}/children`)
  return z.array(agentNodeSchema).parse(response.data).map(toAgentNode)
}

/** Tugun va butun ostidagilar — bitta so'rov (`path <@`). */
export async function fetchAgentSubtree(id: string): Promise<AgentNode[]> {
  const response = await api.get<unknown>(`/api/agents/${id}/subtree`)
  return z.array(agentNodeSchema).parse(response.data).map(toAgentNode)
}

/**
 * BUTUN daraxt yassi ro'yxat sifatida.
 *
 * ⚠️ NEGA `roots` YETMAYDI: ildizlar faqat BIRINCHI qavat. Reseller
 * daraxti ko'p qavatli bo'ladi va aynan pastki qavatlar muhim —
 * "narx otanikidan past bo'lmasin" qoidasi faqat shu yerda ma'noga
 * ega. Faqat ildizlarni ko'rsatish sub-resellerlarni panelda
 * KO'RINMAS qilardi: ular bor, sotadi, lekin narxini qo'yib
 * bo'lmasdi.
 *
 * ⚠️ NEGA `GET /api/agents` EMAS: u `q` siz chaqirilganda ishonchsiz
 * (yuqoridagi `fetchAgentRoots` izohi). `roots` + har ildiz uchun
 * bitta `subtree` — ikkalasi ham sinalgan yo'l, so'rovlar soni esa
 * ildizlar soniga teng (odatda 1-3 ta).
 */
export async function fetchAgentTree(): Promise<AgentNode[]> {
  const roots = await fetchAgentRoots()
  const subtrees = await Promise.all(roots.map((r) => fetchAgentSubtree(r.id)))

  // `subtree` ildizning O'ZINI ham qaytaradi, shuning uchun id bo'yicha
  // yig'amiz — takror qatorlar jadvalda `rowKey` to'qnashuvini berardi.
  const byId = new Map<string, AgentNode>()
  for (const node of [...roots, ...subtrees.flat()]) byId.set(node.id, node)
  return [...byId.values()].sort((a, b) => a.path.localeCompare(b.path))
}

/**
 * Qidiruv natijasi — bir SAHIFA va JAMI son.
 *
 * ⚠️ `total` ekranda kerak: natija sahifadan oshsa jadval to'liq
 * ko'rinardi va operator qolganini "yo'q" deb hisoblardi.
 */
export interface AgentSearchResult {
  nodes: readonly AgentNode[]
  total: number
}

/**
 * Qidiruv. `q` MAJBURIY — yuqoridagi izohga qarang.
 *
 * ⚠️ QIDIRUV PARAMETRI `q`, `search` EMAS. Qolgan oltita sahifalangan
 * ro'yxatda u `search` deb ataladi; bu endpointda backend ataylab `q` ni
 * qoldirdi. Nomni "bir xillik uchun" almashtirish ishlab turgan
 * qidiruvni JIMGINA o'chirardi.
 *
 * ⚠️ Bitta so'rov, `MAX_PAGE_SIZE` qator. Bu jadval EMAS, daraxt
 * sahifasidagi qidiruv ro'yxati: sahifa tugmalari yo'q, shuning uchun
 * kesilgani banner bilan AYTILADI (`AgentsPage`).
 */
export async function searchAgents(q: string): Promise<AgentSearchResult> {
  const response = await api.get<unknown>('/api/agents', {
    params: { q, page: 0, size: MAX_PAGE_SIZE },
  })
  const parsed = serverPageSchema(agentNodeSchema).parse(response.data)
  return { nodes: parsed.rows.map(toAgentNode), total: parsed.total }
}

export async function fetchAgent(id: string): Promise<AgentNode> {
  const response = await api.get<unknown>(`/api/agents/${id}`)
  return toAgentNode(agentNodeSchema.parse(response.data))
}

export interface AgentDraft {
  code: string
  displayName: string
  kind: AgentKind
  settlementMode: SettlementMode
  state?: AgentState
}

export async function createAgent(draft: AgentDraft, parentId?: string | null): Promise<AgentNode> {
  const response = await api.post<unknown>('/api/agents', draft, {
    ...(parentId ? { params: { parentId } } : {}),
  })
  return toAgentNode(agentNodeSchema.parse(response.data))
}

/**
 * Tahrirlash uchun kirish — `AgentDraft` DAN AYRIM va bu ataylab.
 *
 * ⚠️ `kind` BU YERDA YO'Q. Server uni `kind = COALESCE(:kind, kind)` bilan
 * yozadi (`PartyNodeRepository.update`, SQL o'qilgan), ya'ni `null` —
 * «tegilmasin». Turni forma orqali almashtirish IKKI tomonga ham
 * yo'qotish: `RESELLER → AGENT` narx ro'yxatini EGASIZ qoldiradi
 * (ADR-034 migratsiyasi aynan shuning uchun `BOTH` ni `AGENT` emas,
 * `RESELLER` ga o'girgan), teskarisi esa huquq to'plamini kengaytiradi.
 * Ustiga panelda tur MARSHRUTNI belgilaydi (`VISIBLE_KINDS`) — o'zgargan
 * qator joriy ro'yxatdan sababsiz yo'qolardi.
 *
 * ⚠️ `state` MAJBURIY va u forma maydoni EMAS, tugunning JORIY holati.
 * Server `state = :state` ni SHARTSIZ yozadi va bo'sh qiymatni
 * `stateOrDefault()` bilan `ACTIVE` ga aylantiradi — ya'ni «nomini
 * to'g'irladim» degan amal to'xtatilgan yoki YOPILGAN agentni jimgina
 * tiriltirib yuborardi. Holat o'zgartirish `AgentGovernance` da,
 * `POST /{id}/state` orqali.
 *
 * ⚠️ `parentId` ham yo'q — ota almashtirish {@link moveAgent}, chunki u
 * butun shoxning yo'lini qayta hisoblaydi (`AgentDraft` javadoc'i).
 */
export interface AgentEditDraft {
  code: string
  displayName: string
  settlementMode: SettlementMode
  /** Tugunning JORIY holati — yuqoridagi izohga qarang */
  state: AgentState
}

export async function updateAgent(id: string, draft: AgentEditDraft): Promise<AgentNode> {
  const response = await api.put<unknown>(`/api/agents/${id}`, { ...draft, kind: null })
  return toAgentNode(agentNodeSchema.parse(response.data))
}

/**
 * Daraxtda KO'CHIRISH — `newParentId` berilmasa tugun ILDIZGA chiqadi.
 *
 * ⚠️ BU TAHRIR EMAS, ALOHIDA VA XAVFLI AMAL. Backend javadoc'i aniq:
 * «o'z tugunini begona shox ostiga ulash — butun shoxini o'sha yerga
 * OLIB KIRISH demak». Bitta so'rov butun ostidagi tugunlarning `path`
 * ini qayta hisoblaydi va abonentlardagi nusxani
 * (`subscriber.reseller_path`) kaskad bilan yangilaydi, ya'ni doirasi
 * shox bilan cheklangan operatorlar KO'RADIGAN abonentlar to'plami
 * o'zgaradi — hech qanday xatosiz.
 *
 * ⚠️ `newParentId` — QUERY parametri (`@RequestParam`), tana emas.
 * Parametr UMUMAN yuborilmasa server uni `null` deb o'qiydi va tugunni
 * ILDIZGA chiqaradi. Shuning uchun bu yerda `null` holati ANIQ
 * ajratilgan: `undefined` bilan aralashib ketsa «tanlovni bekor qildim»
 * degan harakat jimgina mustaqil qilib yuborardi.
 */
export async function moveAgent(id: string, newParentId: string | null): Promise<AgentNode> {
  const response = await api.post<unknown>(`/api/agents/${id}/move`, undefined, {
    ...(newParentId === null ? {} : { params: { newParentId } }),
  })
  return toAgentNode(agentNodeSchema.parse(response.data))
}

/** Tugun O'CHIRILMAYDI — yopiladi (`DELETE` → `changeState(CLOSED)`) */
export async function closeAgent(id: string): Promise<AgentNode> {
  const response = await api.delete<unknown>(`/api/agents/${id}`)
  return toAgentNode(agentNodeSchema.parse(response.data))
}

/**
 * Holatni o'zgartirish — `ACTIVE` ⇄ `SUSPENDED`, yoki `CLOSED`.
 *
 * ⚠️ BU ENDPOINT BACKEND'DA BOR EDI, LEKIN PANELDA CHAQIRILMAYDI EDI, va
 * bu jimgina yo'qotish edi: yagona mavjud tugma `closeAgent` (ya'ni
 * `DELETE` → `CLOSED`), `CLOSED` esa TERMINAL holat
 * ({@code AgentState} javadoc'i — "yopish qaytarilmaydi"). Demak
 * agentni VAQTINCHA to'xtatmoqchi bo'lgan operatorda faqat
 * QAYTARIB BO'LMAYDIGAN variant bor edi. `SUSPENDED` aynan shu ehtiyoj
 * uchun mavjud: yangi sotuv to'xtaydi, daraxt va tarix joyida qoladi.
 *
 * ⚠️ `state` — QUERY parametri, tana emas (`AgentController.changeState`
 * da `@RequestParam`). Tanada yuborilsa so'rov 400 bilan qaytardi.
 */
export async function setAgentState(id: string, state: AgentState): Promise<AgentNode> {
  const response = await api.post<unknown>(`/api/agents/${id}/state`, undefined, {
    params: { state },
  })
  return toAgentNode(agentNodeSchema.parse(response.data))
}

// --- Oylik limitlar ---------------------------------------------------------
//
// Reseller o'z abonentlari ustida qaytariladigan amallarni cheklovsiz
// bajara olmasligi kerak (`LimitKind` javadoc'i: cheklovsiz MAC
// almashtirish bitta hisobni ko'p qurilmaga aylantiradi). Backend bu
// hisobni YURITADI — lekin panelda uni ko'radigan ham, qo'yadigan ham
// joy yo'q edi, ya'ni himoya sozlanmagan holda turardi.

export const LIMIT_KINDS = ['DELETE', 'RENAME', 'PROFILE_CHANGE', 'MAC_CHANGE'] as const
export type LimitKind = (typeof LIMIT_KINDS)[number]

/**
 * ⚠️ `null` = CHEKSIZ, `0` = UMUMAN RUXSAT YO'Q. Bu ikkisi boshqa narsa
 * va backend ularni hech qachon bir-biriga aylantirmaydi
 * ({@code MonthlyLimits} javadoc'i). Panel ham aylantirmaydi: `??  0`
 * yozish "cheksiz" ni "taqiqlangan" ga o'girib yuborardi.
 *
 * ⚠️ Kalit YETISHMASLIGI ham "cheksiz" degani — backend `null`
 * qiymatlarni xaritaga umuman qo'ymaydi (`MonthlyLimits.copy`).
 */
const monthlyLimitsSchema = z.object({
  limits: z.record(z.string(), z.number()).nullish(),
  used: z.record(z.string(), z.number()).nullish(),
})

export interface MonthlyLimits {
  /** `null` — cheksiz */
  limits: Record<LimitKind, number | null>
  /** Joriy oyda ishlatilgani */
  used: Record<LimitKind, number>
}

function toMonthlyLimits(raw: z.infer<typeof monthlyLimitsSchema>): MonthlyLimits {
  const limits = {} as Record<LimitKind, number | null>
  const used = {} as Record<LimitKind, number>
  for (const kind of LIMIT_KINDS) {
    limits[kind] = raw.limits?.[kind] ?? null
    used[kind] = raw.used?.[kind] ?? 0
  }
  return { limits, used }
}

export async function fetchAgentLimits(id: string): Promise<MonthlyLimits> {
  const response = await api.get<unknown>(`/api/agents/${id}/limits`)
  return toMonthlyLimits(monthlyLimitsSchema.parse(response.data))
}

/**
 * Limitlarni yozadi.
 *
 * ⚠️ BACKEND'DA UCH XIL MA'NO BOR VA ULARNI ARALASHTIRIB BO'LMAYDI
 * (`MonthlyLimitRepository.upsertLimits` — SQL o'qilgan, taxmin emas):
 *
 * <ul>
 *   <li>kalit UMUMAN YO'Q → ustunga TEGILMAYDI (eski qiymat qoladi);</li>
 *   <li>kalit bor, qiymati son → o'sha limit;</li>
 *   <li>kalit bor, qiymati `null` → ustun `NULL`, ya'ni CHEKSIZ.</li>
 * </ul>
 *
 * Shuning uchun bu funksiya HAR TO'RT turni HAR DOIM yuboradi va
 * `null` ni tashlab yubormaydi. Aks holda "cheksiz qildim" degan amal
 * jimgina "hech narsa o'zgartirmadim" ga aylanardi: forma limitni
 * bo'shatgan bo'lardi, ekran esa eski sonni qaytadan ko'rsatardi va
 * operator buni panel nosozligi deb o'ylardi.
 */
export async function setAgentLimits(
  id: string,
  limits: Record<LimitKind, number | null>,
): Promise<MonthlyLimits> {
  const body: Record<string, number | null> = {}
  for (const kind of LIMIT_KINDS) {
    body[kind] = limits[kind] ?? null
  }
  const response = await api.put<unknown>(`/api/agents/${id}/limits`, body)
  return toMonthlyLimits(monthlyLimitsSchema.parse(response.data))
}

// --- Pul limitlari (kunlik va oylik) ----------------------------------------
//
// ⚠️ `LimitKind` BILAN CHALKASHTIRILMAYDI — bu ikki BOSHQA narsa va
// backend ularni ataylab ikki endpointda saqlaydi (`FundLimitKind`
// javadoc'i):
//   `LimitKind`     — AMAL SONI (o'chirish, nom, tarif, MAC), butun son,
//                     RESELLER ning o'z abonentlari ustida;
//   `FundLimitKind` — PUL, MINOR birlikda, AGENT bir kunda/oyda qancha
//                     to'ldira olishi.
// Ikkalasini bitta ekranga yig'ish "dona" va "so'm" ni bir ustunda
// aralashtirardi.

/**
 * ⚠️ `PER_TXN` — OYNA EMAS (egasi qarori, 2026-08-19).
 *
 * `DAILY`/`MONTHLY` — vaqt oynasi: ular yig'iladi va «sarflandi /
 * qoldi» degan savol ma'noli. `PER_TXN` esa BITTA to'lovning shifti:
 * u hech qachon yig'ilmaydi va `used` ustuni unda YO'Q. Shuning uchun
 * uni «10 000 / 100 000» shaklida chizish yolg'on bo'lardi — ekranda
 * u «bitta to'lovda ko'pi bilan …» deb yoziladi.
 *
 * ⚠️ Zinapoya (`TIER`) SHU SHIFT OSTIDA ishlaydi: bitta to'ldirish
 * `PER_TXN` dan oshmaydi, ya'ni undan yuqoridagi pog'onaga summa
 * hech qachon tushmaydi (`deadTierRows`).
 */
export const FUND_LIMIT_KINDS = ['PER_TXN', 'DAILY', 'MONTHLY'] as const
export type FundLimitKind = (typeof FUND_LIMIT_KINDS)[number]

/** `used` tushunchasi bor turlar — qolganida u har doim 0 va ko'rsatilmaydi */
export const WINDOWED_FUND_LIMIT_KINDS: readonly FundLimitKind[] = ['DAILY', 'MONTHLY']

/**
 * ⚠️ `null` = CHEKSIZ, `0` = UMUMAN RUXSAT YO'Q — `MonthlyLimits` dagi
 * bilan AYNAN bir xil qoida (backend uni ataylab takrorlaydi).
 *
 * ⚠️ Kalit YETISHMASLIGI ham "cheksiz" degani: `FundLimits` konstruktori
 * `null` qiymatlarni xaritaga umuman qo'ymaydi.
 */
const fundLimitsSchema = z.object({
  limits: z.record(z.string(), z.number()).nullish(),
  used: z.record(z.string(), z.number()).nullish(),
})

export interface FundLimits {
  /** MINOR birlikda; `null` — cheksiz */
  limits: Record<FundLimitKind, number | null>
  /** Joriy oynada sarflangani, MINOR birlikda */
  used: Record<FundLimitKind, number>
}

function toFundLimits(raw: z.infer<typeof fundLimitsSchema>): FundLimits {
  const limits = {} as Record<FundLimitKind, number | null>
  const used = {} as Record<FundLimitKind, number>
  for (const kind of FUND_LIMIT_KINDS) {
    limits[kind] = raw.limits?.[kind] ?? null
    used[kind] = raw.used?.[kind] ?? 0
  }
  return { limits, used }
}

export async function fetchFundLimits(id: string): Promise<FundLimits> {
  const response = await api.get<unknown>(`/api/agents/${id}/fund-limits`)
  return toFundLimits(fundLimitsSchema.parse(response.data))
}

/**
 * Kunlik/oylik pul limitini yozadi.
 *
 * ⚠️ `setAgentLimits` bilan BIR XIL uch ma'no (`FundLimitRepository`
 * o'qilgan): kalit YO'Q → ustunga tegilmaydi; son → o'sha limit;
 * `null` → cheksiz. Shuning uchun bu funksiya ham HAR IKKI turni HAR
 * DOIM yuboradi — aks holda "cheksiz qildim" jimgina "hech narsa
 * o'zgartirmadim" ga aylanardi.
 *
 * ⚠️ Qiymat — `Money` EMAS, MINOR butun son: server `Map<FundLimitKind,
 * Long>` kutadi. Bu yerda `Money` yuborilsa Jackson uni sonyga
 * aylantira olmay 400 berardi.
 */
export async function setFundLimits(
  id: string,
  limits: Record<FundLimitKind, number | null>,
): Promise<FundLimits> {
  const body: Record<string, number | null> = {}
  for (const kind of FUND_LIMIT_KINDS) {
    body[kind] = limits[kind] ?? null
  }
  const response = await api.put<unknown>(`/api/agents/${id}/fund-limits`, body)
  return toFundLimits(fundLimitsSchema.parse(response.data))
}

// --- Pul --------------------------------------------------------------------

const balanceSchema = z.object({
  balance: moneySchema,
  available: moneySchema,
  debtLimit: debtLimitSchema,
})

export interface AgentBalance {
  balance: Money
  /** Balans + qarz limiti — hozir sarflash mumkin bo'lgan summa */
  available: Money
  /** `null` = limit SOZLANMAGAN (nol limitdan boshqa ma'no) */
  debtLimit: Money | null
}

export async function fetchAgentBalance(id: string): Promise<AgentBalance> {
  const response = await api.get<unknown>(`/api/agents/${id}/balance`)
  const parsed = balanceSchema.parse(response.data)
  return {
    balance: toMoney(parsed.balance),
    available: toMoney(parsed.available),
    debtLimit: toOptionalMoney(parsed.debtLimit),
  }
}

/**
 * Idempotentlik kaliti — pul amallarida MAJBURIY (`@RequestHeader`).
 *
 * ⚠️ KALITNI CHAQIRUVCHI BERADI va bu majburiy parametr — ataylab.
 *
 * Ilgari uni shu yerdagi funksiya har chaqiruvda yangidan yasardi. Javadoc
 * esa "himoya chaqiruvchi tomonda" deb turardi, ya'ni va'da yozilgan, lekin
 * hech narsa uni majburlamasdi: chaqiruvchi kalit bermasdi va tarmoq
 * uzilib qayta bosilganda server ikkita to'lov yozardi. Endi parametrsiz
 * chaqiruv kompilyatsiyada yiqiladi.
 *
 * ⚠️ Ikkinchi nuqson: eski funksiya `crypto.randomUUID()` ni tekshiruvsiz
 * chaqirardi. U faqat XAVFSIZ KONTEKSTDA mavjud, panel esa IP orqali oddiy
 * HTTP ustidan ochiladi — o'sha yerda chaqiruv butun oynani yiqitardi.
 * `shared/lib/idempotency.ts` da zaxira variant bor va endi o'sha
 * ishlatiladi.
 *
 * Kalitni {@link useIdempotencyKey} bilan oling: u kalitni AMAL imzosiga
 * bog'laydi, ya'ni qayta urinishda o'zgarmaydi, summa o'zgarganda esa
 * yangisi yasaladi.
 */
export async function topUpAgent(
  id: string,
  amount: Money,
  idempotencyKey: string,
): Promise<OperationResult> {
  const response = await api.post<unknown>(`/api/agents/${id}/deposit`, amount, {
    headers: { 'Idempotency-Key': idempotencyKey },
  })
  return operationResultSchema.parse(response.data)
}

export async function setDebtLimit(id: string, limit: Money): Promise<OperationResult> {
  const response = await api.put<unknown>(`/api/agents/${id}/debt-limit`, limit)
  return operationResultSchema.parse(response.data)
}

/**
 * Agent abonent balansini to'ldiradi (o'z depozitidan).
 *
 * Mablag' yetmasa javob 200 va ichida `FAILED` — bu texnik xato emas,
 * kutilgan biznes holati.
 */
export async function fundSubscriber(
  agentId: string,
  subscriberId: string,
  amount: Money,
  idempotencyKey: string,
): Promise<OperationResult> {
  const response = await api.post<unknown>(`/api/agents/${agentId}/fund`, amount, {
    params: { subscriberId },
    headers: { 'Idempotency-Key': idempotencyKey },
  })
  return operationResultSchema.parse(response.data)
}

/** Postpaid agent naqd pulni topshirdi */
export async function handOverCash(
  id: string,
  amount: Money,
  idempotencyKey: string,
): Promise<OperationResult> {
  const response = await api.post<unknown>(`/api/agents/${id}/handover`, amount, {
    headers: { 'Idempotency-Key': idempotencyKey },
  })
  return operationResultSchema.parse(response.data)
}

export async function fetchOutstanding(id: string): Promise<Money> {
  const response = await api.get<unknown>(`/api/agents/${id}/outstanding`)
  return toMoney(moneySchema.parse(response.data))
}

// --- Komissiya --------------------------------------------------------------

/**
 * ⚠️ `TIER` — UCHINCHI tur (SHARTNOMA §2, egasi qarori 2026-08-19):
 * «10 000 gacha 2, 10 000 dan 50 000 gacha 5, va davomiy».
 *
 * `TIER` da `percentBp` va `fixedAmount` — `null`, qiymat `tiers[]` da.
 */
export const COMMISSION_KINDS = ['PERCENT', 'FIXED', 'TIER'] as const
export type CommissionKind = (typeof COMMISSION_KINDS)[number]

/**
 * Pog'ona — SHARTNOMA §3 dagi `tiers[]` elementi.
 *
 * ⚠️ `upToAmount` — YUQORI chegara va u KIRITILMAYDI (`summa < upTo`).
 * Oxirgi pog'onada `null` = cheksizlik. «10 000 gacha» → aynan 10 000
 * KEYINGI pog'onaga tushadi va bu yagona talqin (SHARTNOMA §2).
 */
const commissionTierSchema = z.object({
  seq: z.number(),
  upToAmount: moneySchema.nullish(),
  percentBp: z.number().nullish(),
  fixedAmount: moneySchema.nullish(),
})

export interface CommissionTier {
  seq: number
  /** `null` — «va undan yuqorisi» */
  upToAmount: Money | null
  percentBp: number | null
  fixedAmount: Money | null
}

const commissionRuleSchema = z.object({
  id: uuidSchema,
  agentId: uuidSchema,
  planId: uuidSchema.nullish(),
  category: z.string().nullish(),
  kind: z.enum(COMMISSION_KINDS),
  /** Bazis punkt: 250 = 2.5% */
  percentBp: z.number().nullish(),
  fixedAmount: moneySchema.nullish(),
  /*
    ⚠️ `.nullish()` — `.nullable()` EMAS va bu MAJBURIY shakl
    (`tests/null-contract.mjs`): 17 ta servisda `non_null` yoqilgan,
    ya'ni bo'sh maydon JSON'ga umuman yozilmasligi mumkin. Shartnoma
    `[]` va'da qiladi, lekin panel VA'DAGA emas, kelgan shaklga
    moslashadi — aks holda eski backend bilan butun sahifa o'lardi.
    Ilova modelida esa `tiers` HAR DOIM massiv (`?? []`).
  */
  tiers: z.array(commissionTierSchema).nullish(),
  validFrom: instantSchema.nullish(),
  validTo: instantSchema.nullish(),
})

export interface CommissionRule {
  id: string
  planId: string | null
  category: string | null
  kind: CommissionKind
  percentBp: number | null
  fixedAmount: Money | null
  /** PERCENT/FIXED da BO'SH RO'YXAT — hech qachon `null` emas */
  tiers: CommissionTier[]
  validFrom: string | null
  validTo: string | null
}

/** ⚠️ ALOHIDA EKSPORT — qorovul testi mantiqning nusxasini emas, SHUNI chaqiradi */
export function toCommissionRule(raw: z.infer<typeof commissionRuleSchema>): CommissionRule {
  return {
    id: raw.id,
    planId: raw.planId ?? null,
    category: raw.category ?? null,
    kind: raw.kind,
    percentBp: raw.percentBp ?? null,
    fixedAmount: raw.fixedAmount ? toMoney(raw.fixedAmount) : null,
    tiers: (raw.tiers ?? [])
      .map((tier) => ({
        seq: tier.seq,
        upToAmount: tier.upToAmount ? toMoney(tier.upToAmount) : null,
        percentBp: tier.percentBp ?? null,
        fixedAmount: tier.fixedAmount ? toMoney(tier.fixedAmount) : null,
      }))
      // ⚠️ Tanlash `seq` bo'yicha TARTIBLANGAN ro'yxatda ishlaydi
      // (SHARTNOMA §2). Server tartibiga ishonib qolish — «10 000 gacha 2»
      // ni «10 000 gacha 5» ga aylantiradigan yagona qadam.
      .sort((a, b) => a.seq - b.seq),
    validFrom: raw.validFrom ?? null,
    validTo: raw.validTo ?? null,
  }
}

export function parseCommissionRules(data: unknown): CommissionRule[] {
  return z.array(commissionRuleSchema).parse(data).map(toCommissionRule)
}

export async function fetchCommissionRules(agentId: string): Promise<CommissionRule[]> {
  const response = await api.get<unknown>(`/api/agents/${agentId}/commission-rules`)
  return parseCommissionRules(response.data)
}

/** SHARTNOMA §3: draft'da `seq` YO'Q — tartib ro'yxatning o'zi. */
export interface CommissionTierDraft {
  upToAmount: Money | null
  percentBp: number | null
  fixedAmount: Money | null
}

export interface CommissionRuleDraft {
  agentId: string
  kind: CommissionKind
  /** PERCENT da to'ldiriladi (0–10000 bp). Qolganida `null` BO'LISHI SHART. */
  percentBp?: number | null
  /** FIXED da to'ldiriladi. Qolganida `null` BO'LISHI SHART. */
  fixedAmount?: Money | null
  /** TIER da to'ldiriladi. Qolganida BO'SH RO'YXAT. */
  tiers?: readonly CommissionTierDraft[]
  /** ISO-8601; `null` — HOZIRDAN (SHARTNOMA §5) */
  validFrom?: string | null
}

/**
 * Komissiya qoidasini o'rnatadi.
 *
 * ⚠️ `planId` va `category` YUBORILMAYDI — ular SHARTNOMA §1 bo'yicha
 * majburiy `null`. Komissiya faqat BALANS TO'LDIRISHDA hisoblanadi,
 * to'ldirishda esa paket tushunchasi YO'Q. Server paketga bog'langan
 * qoidani endi 400 bilan rad etadi.
 *
 * ⚠️ HAR uch qiymat kaliti HAR DOIM yoziladi, keraksizi ANIQ `null`:
 * backend `CommissionRuleDraft` konstruktori ikkitasi to'ldirilgan
 * bo'lsa `AgentException.Invalid` beradi, kalitni tashlab ketish esa
 * «tegilmadi» degan uchinchi ma'noni ochardi.
 *
 * ⚠️ `tiers` PERCENT/FIXED da BO'SH RO'YXAT (`[]`), `null` EMAS —
 * SHARTNOMA §3 ning aynan matni.
 */
export function commissionRuleBody(draft: CommissionRuleDraft): Record<string, unknown> {
  return {
    agentId: draft.agentId,
    planId: null,
    category: null,
    kind: draft.kind,
    percentBp: draft.kind === 'PERCENT' ? (draft.percentBp ?? null) : null,
    fixedAmount: draft.kind === 'FIXED' ? (draft.fixedAmount ?? null) : null,
    tiers: draft.kind === 'TIER' ? [...(draft.tiers ?? [])] : [],
    validFrom: draft.validFrom ?? null,
  }
}

export async function setCommissionRule(draft: CommissionRuleDraft): Promise<CommissionRule> {
  const response = await api.put<unknown>(
    `/api/agents/${draft.agentId}/commission-rules`,
    commissionRuleBody(draft),
  )
  return toCommissionRule(commissionRuleSchema.parse(response.data))
}


/**
 * Komissiya qoidasini bekor qiladi (BUG-020).
 *
 * ⚠️ Server qatorni O'CHIRMAYDI — `valid_to` qo'yib YOPADI, chunki shu
 * qoida bo'yicha hisoblangan komissiyalar jurnalda turibdi va ular
 * qaysi shart asosida chiqqanini tushuntirib bera olish kerak. Ya'ni
 * yopilgan qoida ro'yxatdan YO'QOLMAYDI, "tugagan" bo'lib qoladi —
 * panel uni shunday ko'rsatishi kerak, aks holda operator tugmani
 * bosadi va "hech narsa o'chmadi" deb o'ylaydi.
 *
 * ⚠️ Bu «0% qilib qo'yish» EMAS: paketga xos 0% qoida umumiy qoidani
 * ham to'sadi, yopish esa umumiy qoidaga QAYTARADI.
 */
export async function closeCommissionRule(agentId: string, ruleId: string): Promise<CommissionRule> {
  const response = await api.delete<unknown>(`/api/agents/${agentId}/commission-rules/${ruleId}`)
  return toCommissionRule(commissionRuleSchema.parse(response.data))
}
